import { useState, useEffect, useCallback, useMemo } from 'react';
import { WorkOrder, WorkOrderStatus } from '../../types/workOrders';
import { workOrdersApi } from '../../api/workOrdersApi';

interface UseProjectWorkOrdersResult {
    workOrders: WorkOrder[];
    activeWorkOrders: WorkOrder[];
    archivedWorkOrders: WorkOrder[];
    unlinkedWorkOrders: WorkOrder[];
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    linkWorkOrder: (workOrderKey: string) => Promise<void>;
    unlinkWorkOrder: (workOrderKey: string) => Promise<void>;
}

export function useProjectWorkOrders(
    buildingKey: string,
    projectKey: string
): UseProjectWorkOrdersResult {
    const [allBuildingWorkOrders, setAllBuildingWorkOrders] = useState<WorkOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchWorkOrders = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Fetch both active and archived work orders
            const [activeWos, archivedWos] = await Promise.all([
                workOrdersApi.getByBuilding(buildingKey),
                workOrdersApi.getArchivedByBuilding(buildingKey)
            ]);
            setAllBuildingWorkOrders([...activeWos, ...archivedWos]);
        } catch (err) {
            setError('Kunde inte ladda ärenden');
            console.error('Failed to fetch work orders:', err);
        } finally {
            setLoading(false);
        }
    }, [buildingKey]);

    useEffect(() => {
        fetchWorkOrders();
    }, [fetchWorkOrders]);

    // Filter client-side for this project
    const workOrders = useMemo(() =>
        allBuildingWorkOrders.filter(wo => wo.projectKey === projectKey),
        [allBuildingWorkOrders, projectKey]
    );

    const activeWorkOrders = useMemo(() =>
        workOrders.filter(wo => wo.status === WorkOrderStatus.OPEN),
        [workOrders]
    );

    const archivedWorkOrders = useMemo(() =>
        workOrders.filter(wo => wo.status !== WorkOrderStatus.OPEN),
        [workOrders]
    );

    // Unlinked work orders (open, no project) for "Koppla ärende"
    const unlinkedWorkOrders = useMemo(() =>
        allBuildingWorkOrders.filter(wo =>
            wo.status === WorkOrderStatus.OPEN && !wo.projectKey
        ),
        [allBuildingWorkOrders]
    );

    const linkWorkOrder = useCallback(async (workOrderKey: string) => {
        await workOrdersApi.linkToProject(workOrderKey, projectKey);
        await fetchWorkOrders();
    }, [projectKey, fetchWorkOrders]);

    const unlinkWorkOrder = useCallback(async (workOrderKey: string) => {
        await workOrdersApi.linkToProject(workOrderKey, null);
        await fetchWorkOrders();
    }, [fetchWorkOrders]);

    return {
        workOrders,
        activeWorkOrders,
        archivedWorkOrders,
        unlinkedWorkOrders,
        loading,
        error,
        refetch: fetchWorkOrders,
        linkWorkOrder,
        unlinkWorkOrder
    };
}
